import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';

const bgDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark community exterior',
    },
  },
};

export function AboutHero() {
  const bg = useSlotGroup('abouthero.background', bgDef);

  return (
    <section className="relative flex h-[min(70svh,684px)] w-full flex-col justify-end overflow-clip bg-[rgb(var(--ala-dark))] md:h-[684px]">
      <EditableSlotGroup groupPath="abouthero.background" definition={bgDef}>
        <div className="absolute inset-0 z-0">
          <VibesitesImage
            url={
              bg.image ||
              ''
            }
            alt={bg.alt || ''}
            priority
            sizes="100vw"
            className="h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(91.65deg, rgb(var(--ala-primary)/1) 0%, rgba(0, 0, 0, 0) 100%)',
            }}
          />
        </div>
      </EditableSlotGroup>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-12 md:px-[clamp(16px,6vw,87px)] md:pb-[25px]"
      >
        <div className="mb-4 flex items-center gap-3">
          <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
          <RichTextContent
            path="abouthero.eyebrow"
            label="Eyebrow"
            as="span"
            className="font-heading text-base font-medium uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-on-primary))]"
          />
        </div>
        <RichTextContent
          path="abouthero.title"
          label="Title"
          as="h1"
          className="font-heading text-[clamp(55px,6.94vw,100px)] font-bold leading-[1.35] text-[rgb(var(--ala-on-primary))]"
        />
      </motion.div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AboutHero',
  displayName: 'About Hero',
  tokens: {
    'abouthero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>american landmark history</p>',
    },
    'abouthero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>About Us</p>',
    },
  },
  slotGroups: {
    'abouthero.background': bgDef,
  },
};

(AboutHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AboutHero;
